import React, { useEffect, useMemo, useState } from "react";
import { useImmer } from "use-immer";
import { flatMap, groupBy, keys, sortBy } from "lodash";
import { useToast } from "../ui/Toast";

import {
  createShoppingList,
  orderRewe,
  ShoppingListCreationDto,
  updateShoppingLists,
} from "../../api";
import {
  queryKeys,
  useDepartments,
  useShops,
  useShoppingList,
} from "../../queries/queries";
import {
  useAvailabilitiesByPurchases,
  usePurchasesByIds,
} from "../../queries/caches";
import { useQueryClient } from "@tanstack/react-query";
import { ShoppingListWithEditedFlags } from "../../utils/editUtils";
import {
  formatPurchaseDescription,
  getDefaultPurchaseUnit,
} from "../../utils/purchaseUtils";
import { ConfirmModal } from "../ui/ConfirmModal";
import {
  showValidationMessage,
  showWarningMessage,
} from "../showValidationMessage";
import { Accordion } from "../ui/Accordion";
import { DepartmentGroup } from "./DepartmentGroup";
import { ShopOption, ShopSelectorDropdown } from "./ShopSelectorDropdown";
import { Plan2PlateIcon } from "../Plan2PlateIcon";
import { cn } from "../../lib/utils";
import { useNavigate } from "react-router-dom";

type DeptGroup = {
  department: string;
  departmentLabel: string;
  items: ShoppingListWithEditedFlags[];
};

type ShopGroup = {
  shopId: number;
  departments: DeptGroup[];
};

const ShoppingListDrawerContent = (props: { onFinish?: () => void }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const shoppingList = useShoppingList();
  const shops = useShops();
  const departments = useDepartments();
  const purchaseIds = useMemo(
    () => [
      ...new Set(
        shoppingList
          .map((item) => item.availabilityPurchaseId)
          .filter((id): id is number => id != null && id > 0),
      ),
    ],
    [shoppingList],
  );
  const purchases = usePurchasesByIds(purchaseIds);
  const availabilities = useAvailabilitiesByPurchases(purchaseIds);
  const [isOpen, setOpen] = useState(false);

  const availabilitiesByPurchaseId = useMemo(() => {
    const map = new Map<number, typeof availabilities>();
    availabilities.forEach((a) => {
      const arr = map.get(a.purchaseId) ?? [];
      arr.push(a);
      map.set(a.purchaseId, arr);
    });
    return map;
  }, [availabilities]);

  const [shopGroups, updateShopGroups] = useImmer<ShopGroup[]>([]);

  // Which shop-cards are expanded
  const [openShopIds, setOpenShopIds] = useState<Set<number>>(new Set());
  // Which department-accordion values are open, keyed by "shopId-dept"
  const [openDepts, setOpenDepts] = useState<Set<string>>(new Set());
  // Track initial load so we don't reset open state on every auto-save reload
  const initializedRef = React.useRef(false);

  // Build grouped structure: shop → departments
  useEffect(() => {
    if (!shops.length || !purchases.length) return;

    const byShop = groupBy(shoppingList, (s) => s.availabilityShopId);
    const groups: ShopGroup[] = sortBy(keys(byShop), Number).map(
      (shopIdStr) => {
        const shopId = parseInt(shopIdStr);
        const shopItems = byShop[shopIdStr];
        const deptMap = groupBy(shopItems, (s) => s.availabilityDepartment);
        const deptGroups: DeptGroup[] = sortBy(keys(deptMap)).map((dept) => ({
          department: dept,
          departmentLabel:
            departments.find((d) => d.name === dept)?.description ?? dept,
          items: sortBy(
            deptMap[dept],
            (s) =>
              purchases.find((p) => p.id === s.availabilityPurchaseId)
                ?.description,
          ) as ShoppingListWithEditedFlags[],
        }));
        return { shopId, departments: deptGroups };
      },
    );

    updateShopGroups(groups);

    // Only initialize open state on first load — subsequent reloads (auto-save) must not reset it
    if (!initializedRef.current && groups.length > 0) {
      initializedRef.current = true;
      setOpenShopIds(new Set(groups.map((g) => g.shopId)));
      setOpenDepts(
        new Set(
          groups.flatMap((g) =>
            g.departments.map((d) => `${g.shopId}-${d.department}`),
          ),
        ),
      );
    }
  }, [shops, shoppingList, purchases, departments]);

  // Auto-save edited items
  useEffect(() => {
    const edited = flatMap(shopGroups, (sg) =>
      flatMap(sg.departments, (d) => d.items),
    ).filter((s) => s.edited);
    if (edited.length > 0) {
      updateShoppingLists({ body: edited }).then(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.shoppingList });
      });
    }
  }, [shopGroups]);

  const updateItemAmount = (itemId: number, amountPurchased: number) => {
    updateShopGroups((draft) => {
      for (const sg of draft) {
        for (const dept of sg.departments) {
          const item = dept.items.find((i) => i.id === itemId);
          if (item) {
            item.amountPurchased = amountPurchased;
            item.edited = true;

            // auto-close department when all items purchased
            const allPurchased = dept.items.every(
              (i) =>
                (i.id === itemId ? amountPurchased : i.amountPurchased) >=
                i.amount,
            );
            if (allPurchased) {
              const key = `${sg.shopId}-${dept.department}`;
              setOpenDepts((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
              });
            }
          }
        }
      }
    });
  };

  const toggleShop = (shopId: number) => {
    setOpenShopIds((prev) => {
      const next = new Set(prev);
      if (next.has(shopId)) next.delete(shopId);
      else next.add(shopId);
      return next;
    });
  };

  // Items without Rewe online purchase
  const itemsWithoutRewe = useMemo(
    () =>
      flatMap(shopGroups, (sg) =>
        flatMap(sg.departments, (d) => d.items),
      ).filter((s) => !s.reweOnlinePurchaseId),
    [shopGroups],
  );

  // Items not fully purchased
  const notFullyPurchased = useMemo(
    () =>
      flatMap(shopGroups, (sg) =>
        flatMap(sg.departments, (d) =>
          d.items.filter((i) => i.amountPurchased < i.amount),
        ),
      ),
    [shopGroups],
  );

  // "In einem anderen Laden den Rest kaufen"
  const [restShopIds, setRestShopIds] = useState<number[]>([]);
  const navigate = useNavigate();

  const currentShopIdSet = useMemo(
    () => new Set(shopGroups.map((g) => g.shopId)),
    [shopGroups],
  );

  // Shops NOT already in the list that cover at least one missing item
  const elsewhereShopOptions: ShopOption[] = useMemo(() => {
    if (!notFullyPurchased.length) return [];
    return shops
      .filter((s) => !currentShopIdSet.has(s.id))
      .map((shop) => {
        const coverCount = notFullyPurchased.filter((item) =>
          (
            availabilitiesByPurchaseId.get(item.availabilityPurchaseId) || []
          ).some((a) => a.shopId === shop.id),
        ).length;
        return {
          id: shop.id,
          description: shop.description,
          availableCount: coverCount,
          totalCount: notFullyPurchased.length,
        };
      })
      .filter((s) => s.availableCount > 0)
      .sort((a, b) => b.availableCount - a.availableCount);
  }, [notFullyPurchased, availabilitiesByPurchaseId, shops, currentShopIdSet]);

  // Virtual shop groups for selected elsewhere shops
  const elsewhereShopGroups = useMemo(() => {
    return restShopIds
      .map((shopId) => {
        const shop = shops.find((s) => s.id === shopId);
        if (!shop) return null;
        const availableItems = notFullyPurchased.filter((item) =>
          (
            availabilitiesByPurchaseId.get(item.availabilityPurchaseId) || []
          ).some((a) => a.shopId === shopId),
        );
        const byDept = groupBy(availableItems, (item) => {
          const avail = (
            availabilitiesByPurchaseId.get(item.availabilityPurchaseId) || []
          ).find((a) => a.shopId === shopId);
          return avail?.department?.name ?? "OTHER";
        });
        const deptGroups: DeptGroup[] = sortBy(keys(byDept)).map((dept) => ({
          department: dept,
          departmentLabel:
            departments.find((d) => d.name === dept)?.description ?? dept,
          items: byDept[dept] as ShoppingListWithEditedFlags[],
        }));
        return { shopId, shopName: shop.description, departments: deptGroups };
      })
      .filter(Boolean) as {
      shopId: number;
      shopName: string;
      departments: DeptGroup[];
    }[];
  }, [
    restShopIds,
    notFullyPurchased,
    availabilitiesByPurchaseId,
    shops,
    departments,
  ]);

  async function doOrder() {
    const orderResult = await orderRewe();
    const notFound = orderResult.data.decisions.filter(
      (d) => d.productName == null,
    );
    const continueOrder = () => {
      if (orderResult.data.error) {
        showValidationMessage(toast, orderResult.data.error);
      } else if (!orderResult.data.url) {
        showValidationMessage(toast, "Unbekannter Fehler");
      } else {
        window.location.href = orderResult.data.url;
      }
    };
    if (notFound?.length) {
      showWarningMessage(
        toast,
        `Folgende Lebensmittel wurden nicht gefunden: ${notFound
          .map((d) => d.reweRequirement.name)
          .join(", ")}`,
        { duration: null, onCloseComplete: continueOrder },
      );
    } else {
      continueOrder();
    }
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {shopGroups.map(({ shopId, departments: deptGroups }) => {
          const shop = shops.find((s) => s.id === shopId);
          if (!shop) return null;

          const allItems = flatMap(deptGroups, (d) => d.items);
          const purchasedCount = allItems.filter(
            (i) => i.amountPurchased >= i.amount,
          ).length;
          const isExpanded = openShopIds.has(shopId);

          return (
            <div
              key={shopId}
              className="rounded-card border border-line bg-surface overflow-hidden"
            >
              {/* Shop-Header (klappbar) */}
              <button
                type="button"
                onClick={() => toggleShop(shopId)}
                className="flex w-full items-center justify-between px-4 py-3"
              >
                <h2 className="text-base font-bold text-ink">
                  {shop.description}
                </h2>
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "text-xs font-medium",
                      purchasedCount === allItems.length
                        ? "text-teal"
                        : "text-ink-muted",
                    )}
                  >
                    {purchasedCount}/{allItems.length}
                  </span>
                  <Plan2PlateIcon
                    icon={isExpanded ? "viewArrow" : "viewArrow"}
                    size="xs"
                  />
                </div>
              </button>

              {/* Abteilungen */}
              {isExpanded && (
                <div className="border-t border-line px-3 pb-3 pt-2">
                  <Accordion
                    value={Array.from(openDepts).filter((k) =>
                      k.startsWith(`${shopId}-`),
                    )}
                    onValueChange={(vals) => {
                      setOpenDepts((prev) => {
                        const next = new Set(prev);
                        // Remove all dept keys for this shop, then add the new ones
                        for (const k of prev) {
                          if (k.startsWith(`${shopId}-`)) next.delete(k);
                        }
                        vals.forEach((v) => next.add(v));
                        return next;
                      });
                    }}
                    className="gap-2"
                  >
                    {deptGroups.map(
                      ({ department, departmentLabel, items }) => (
                        <DepartmentGroup
                          key={`${shopId}-${department}`}
                          value={`${shopId}-${department}`}
                          department={department}
                          departmentLabel={departmentLabel}
                          items={items}
                          purchases={purchases}
                          onItemChange={updateItemAmount}
                        />
                      ),
                    )}
                  </Accordion>
                </div>
              )}
            </div>
          );
        })}

        {/* In einem anderen Laden den Rest kaufen */}
        {notFullyPurchased.length > 0 && elsewhereShopOptions.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-ink-muted px-1">
              In einem anderen Laden den Rest kaufen
            </p>
            <ShopSelectorDropdown
              shops={elsewhereShopOptions}
              selectedIds={restShopIds}
              onSelectionChange={(ids) => {
                setRestShopIds(ids);
                // Open cards for newly selected shops
                setOpenShopIds((prev) => {
                  const next = new Set(prev);
                  ids.forEach((id) => next.add(id));
                  return next;
                });
                setOpenDepts((prev) => {
                  const next = new Set(prev);
                  ids.forEach((shopId) => {
                    const group = elsewhereShopGroups.find(
                      (g) => g.shopId === shopId,
                    );
                    group?.departments.forEach((d) =>
                      next.add(`elsewhere-${shopId}-${d.department}`),
                    );
                  });
                  return next;
                });
              }}
            />
          </div>
        )}

        {/* Einkauf beenden */}
        <button
          type="button"
          onClick={async () => {
            if (restShopIds.length) {
              const otherShop = restShopIds[0];
              const request: ShoppingListCreationDto = {
                selectedShops: [otherShop],
                shoppingListRequirementDtos: notFullyPurchased.map((item) => ({
                  purchaseUnitId: getDefaultPurchaseUnit(
                    purchases.find(
                      (p) => p.id === item.availabilityPurchaseId,
                    )!,
                  ).id,
                  amount: item.amount - item.amountPurchased,
                  amountInStock: 0,
                  mealDetails: [],
                })),
              };
              await createShoppingList({ body: request });
              window.location.reload();
            } else if (props.onFinish) {
              props.onFinish();
            } else {
              navigate("/plan");
            }
          }}
          className="w-full rounded-card bg-teal py-3 text-sm font-bold text-white transition"
        >
          {restShopIds.length
            ? `In ${shops.find((s) => s.id === restShopIds[0])?.description} einkaufen`
            : "Einkauf beenden"}
        </button>
      </div>

      <ConfirmModal
        icon={"shopCart"}
        onCancel={() => setOpen(false)}
        onConfirm={async () => {
          setOpen(false);
          await doOrder();
        }}
        open={isOpen}
        title={"Fehlende Verfügbarkeiten"}
        confirmLabel={"Bestätigen"}
        description={`Für folgende Lebensmittel sind keine Verfügbarkeiten definiert: ${itemsWithoutRewe
          .map((s) => s.availabilityPurchaseId)
          .filter(Boolean)
          .map((id) => purchases.find((p) => p.id === id))
          .map(formatPurchaseDescription)
          .join(", ")}. Bitte erzeugte Bestellung prüfen.`}
      />
    </>
  );
};

export default ShoppingListDrawerContent;
